import React, { useState, useEffect } from 'react';
import { FaPlus, FaSave, FaEdit, FaTrash } from "react-icons/fa";

import Dialog from "../FormDialog/Dialog";
import { getUserInfo } from "../../../services/authService-sighup";
import { METRIC_TAB } from '../constants/constants';
import apiClientAdmin from "../../../services/apiClientAdmin";
import { getHeaders as getAssetHeaders } from "../../ApiHeaders/apiHeaders";
import VendorSourceMultiSelect from "./VendorSourceMultiSelect";



import MetricGroupISODropdown from '../../DropDowns/MetricGroupISODropdown/MetricGroupISODropdown';
import FuelTypeDropdown from "../../DropDowns/FuelTypeDropdown/FuelTypeDropdown";
import CategoryDropdown from "../../DropDowns/CategoryDropdown/CategoryDropdown";
import SourceDropdown from "../../DropDowns/SourceDropdown/SourceDropdown";
import AwardTypeDropdown from "../../DropDowns/AwardTypeDropdown/AwardTypeDropdown";
import ProductTypeDropdown from "../../DropDowns/ProductTypeDropdown/ProductTypeDropdown";
import MarketTypeDropdown from "../../DropDowns/MarketTypeDropdown/MarketTypeDropdown";
import BaseGranularityDropdown from "../../DropDowns/BaseGranularityDropdown/BaseGranularityDropdown";
import BaseEntityDropdown from "../../DropDowns/BaseEntityDropdown/BaseEntityDropdown";
import UnitsDropdown from "../../DropDowns/UnitsDropdown/UnitsDropdown";
import MetricInternalKeyDropdown from "../../DropDowns/MetricInternalKeyDropdown/MetricInternalKeyDropdown";
import StatusDropdown from "../../DropDowns/StatusDropdown/StatusDropdown";



interface MetadataType {
    award_types: any[];
    product_types: any[];
    agg_functions: {
    agg_func_id: number;
    category: string;
    from_granularity: string;
    to_granularity: string;
    agg_method: string;
    description: string;
    unit: string;
    }[];
}

interface VendorSource {
    vendor_source_id: number;
    vendor_source_name: string;
}

interface Frequency {
    code: string;
    label: string;
}

interface VendorMapping {
    vendor_source_id: number;
    vendor_table: string;
    data_collection_frequency: string;
}

const SOURCE_LABELS: Record<string, string> = {
    nautilus: "Snowflake",
    pi: "PI",
};

// One sentence covering every row a save touched, e.g.
// 'Metric "RT_SOC" successfully updated for Snowflake, removed for PI.'
const describeSave = (
    name: string,
    created: string[],
    updated: string[],
    removed: string[] = []
) => {
    const parts: string[] = [];
    if (updated.length) parts.push(`updated for ${updated.join(" and ")}`);
    if (created.length) parts.push(`added for ${created.join(" and ")}`);
    if (removed.length) parts.push(`removed for ${removed.join(" and ")}`);
    return `Metric "${name}" successfully ${parts.join(", ")}.`;
};

interface MetricFormProps {
    metric?: any;
    metadata: MetadataType | null;
    selectedMetric: any | null;
    setSelectedMetric: (metric: any) => void;
    onClearSelection: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onSaveMetric: (newMetric: any, opts?: { silent?: boolean }) => Promise<any | false>;
    onSaveChanges: (updatedMetric: any, opts?: { silent?: boolean }) => Promise<boolean>;
    onDeleteMetric: (metricId: number) => Promise<boolean>;
    onSaveSuccess: (message: string) => void;
    handleApiError: (err: any, fallbackTitle: string) => void;
    onModeChange: (mode: 'create' | 'edit' | 'view' | null) => void;
    canDelete?: boolean;
  }

const MetricForm: React.FC<MetricFormProps> = ({
    metric,
    metadata,
    selectedMetric,
    setSelectedMetric,
    onClearSelection,
    onDirtyChange,
    onSaveMetric,
    onSaveChanges,
    onDeleteMetric,
    onSaveSuccess,
    handleApiError,
    onModeChange,
    canDelete = false
  }) => {


    const [iso_code, setIso_code] = useState(metric?.iso_code || '');
    const [metric_name, setmetric_name] = useState(metric?.metric_name || '');
    const [description, setdescription] = useState(metric?.description || '');
    const [units, setunits] = useState(metric?.units || '');
    const [time_to_live, settime_to_live] = useState<number>(metric?.time_to_live ?? 5400);
    const [category, setcategory] = useState(metric?.category || '');
    const [award_type, setaward_type] = useState(metric?.award_type || '');
    const [market_type, setmarket_type] = useState(metric?.market_type || '');
    const [base_granularity, setbase_granularity] = useState(metric?.base_granularity || '');
    const [fuel_type, setfuel_type] = useState(metric?.fuel_type || '');

    const [metric_internal_key, setmetric_internal_key] = useState(metric?.metric_internal_key || '');
    const [pi_metric_internal_key, setPiMetricInternalKey] = useState('');

    const [base_entity_name, setbase_entity_name] = useState(metric?.base_entity_name || '');
    const [source, setsource] = useState<string[]>([]);
    const [vendorSources, setVendorSources] = useState<VendorSource[]>([]);
    const [frequencies, setFrequencies] = useState<Frequency[]>([]);
    const [vendorMappings, setVendorMappings] = useState<VendorMapping[]>([]);
    const [originalVendorMappings, setOriginalVendorMappings] = useState<VendorMapping[]>([]);

    // Snapshot of the sources that actually have a row stored. Set only when a
    // metric is loaded, never by user toggles, so pending adds and removals can
    // be derived from it at save time.
    const [originalSources, setOriginalSources] = useState<string[]>([]);

    // Sources with a stored row that the user has un-ticked — deleted on Save.
    const pendingSourceRemovals = originalSources.filter(
        src => !source.includes(src)
    );
    const [agg_func_id, setagg_func_id] = useState(metric?.agg_func_id || '');
    const [product, setproduct] = useState(metric?.product || '');
    const [settle_charge_code, setsettle_charge_code] = useState(metric?.settle_charge_code || '');
    const [settle_charge_name, setsettle_charge_name] = useState(metric?.settle_charge_name || '');
    

    const [status, setStatus] = useState<string>("");
    useEffect(() => {
        if (selectedMetric && selectedMetric.length > 0) {
        setStatus(selectedMetric[0].status); 
        }
    }, [selectedMetric]);
      


    // A metric published with status ALL has a frozen name and description and
    // cannot be deleted. Every other field stays editable, and its individual
    // sources can still be detached. All of this is enforced server-side too.
    const isStatusAll =
        !!selectedMetric && (status || "").toUpperCase() === "ALL";

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [isViewMode, setIsViewMode] = useState(false);  
    const [isEditMode, setIsEditMode] = useState(false);
    // Without admin the form is view-only: every field stays disabled.
    const fieldsLocked = !canDelete || (!!selectedMetric && !isEditMode);
    const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>({});
    const [activeField, setActiveField] = useState<string | null>(null);



    useEffect(() => {
        const fetchUserInfo = () => {
        const userInfo = getUserInfo();
        if (userInfo) {
            setUser({
            fullName: userInfo.fullName || "Unknown User",
            email: userInfo.email || "",
            });
        }
        };
        fetchUserInfo();
    }, []);

    const [user, setUser] = useState<{ fullName: string; email: string } | null>(null);
    const createdBy = user ? `${user.email || user.fullName}` : "Unknown User";
    const modifiedBy = user ? `${user.email || user.fullName}` : "Unknown User";




    const [dialog, setDialog] = useState<{
        open: boolean;
        title: string;
        message: React.ReactNode;
        confirmButtonText: string;
        cancelButtonText?: string;
        onConfirm: () => void;
        onCancel?: () => void;
    }>({
        open: false,
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
    });

    const closeDialog = () => {
        setDialog({
        open: false,
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
        });
    };


    useEffect(() => {
        if (!selectedMetric) return;

        // FORCE EXIT CREATE / EDIT MODE
        setIsEditMode(false);
        setIsViewMode(true);

        // CLEAR MODIFIED FLAGS
        setModifiedFields({});
        onDirtyChange(false);

        const metrics = Array.isArray(selectedMetric)
            ? selectedMetric
            : [selectedMetric];

        setOriginalSources(metrics.map(m => m.source));

        const nautilusMetric = metrics.find(m => m.source === "nautilus");
        const piMetric = metrics.find(m => m.source === "pi");
        const primary = nautilusMetric || metrics[0];

        // Populate fields
        setIso_code(primary.iso_code || "");
        setmetric_name(primary.metric_name || "");
        setdescription(primary.description || "");
        setunits(primary.units || "");
        settime_to_live(primary.time_to_live ?? 5400);
        setcategory(primary.category || "");
        setaward_type(primary.award_type || "");
        setmarket_type(primary.market_type || "");
        setbase_granularity(primary.base_granularity || "");
        setfuel_type(primary.fuel_type || "");
        setmetric_internal_key(primary.metric_internal_key || "");
        setbase_entity_name(primary.base_entity_name || "");
        setagg_func_id(primary.agg_func_id || "");
        setproduct(primary.product || "");
        setsettle_charge_code(primary.settle_charge_code || "");
        setsettle_charge_name(primary.settle_charge_name || "");

        setsource(metrics.map(m => m.source));
        setPiMetricInternalKey(piMetric?.metric_internal_key || "");
    }, [selectedMetric]);

    useEffect(() => {
        const fetchVendorOptions = async () => {
            try {
                const headers = await getAssetHeaders();
                const [sourcesResponse, frequenciesResponse] = await Promise.all([
                    apiClientAdmin.get("/ref-data/vendor-sources/", { headers }),
                    apiClientAdmin.get("/ref-data/vendor-sources/data-collection-frequencies", { headers }),
                ]);
                setVendorSources(sourcesResponse?.data?.response?.data || []);
                setFrequencies(frequenciesResponse?.data?.response?.data || []);
            } catch (err) {
                handleApiError(err, "Failed to fetch vendor source options");
            }
        };

        fetchVendorOptions();
    }, []);

    useEffect(() => {
        const primaryMetric = Array.isArray(selectedMetric) ? selectedMetric[0] : selectedMetric;
        if (!primaryMetric?.metric_id) {
            setVendorMappings([]);
            setOriginalVendorMappings([]);
            return;
        }

        const fetchVendorMappings = async () => {
            try {
                const headers = await getAssetHeaders();
                const response = await apiClientAdmin.get(
                    `/ref-data/metric-config/${primaryMetric.metric_id}/vendor-sources`,
                    { headers }
                );
                const mappings = (response?.data?.response?.data || []).map((mapping: VendorMapping) => ({
                    vendor_source_id: mapping.vendor_source_id,
                    vendor_table: mapping.vendor_table || "",
                    data_collection_frequency: mapping.data_collection_frequency || "",
                }));
                setVendorMappings(mappings);
                setOriginalVendorMappings(mappings);
            } catch (err) {
                handleApiError(err, "Failed to fetch metric vendor sources");
            }
        };

        fetchVendorMappings();
    }, [selectedMetric]);

    

    useEffect(() => {
        // Only auto-fill when creating a new metric
        if (selectedMetric) return;    
        // if (!isEditMode) return;       
        if (!market_type || !product || !category) return;
    
        const autoValue = `${market_type}_${product}_${category}`;
    
        setsettle_charge_name((prev: string) => {
            if (modifiedFields.settle_charge_name) return prev;
            return autoValue;
        });
    }, [market_type, product, category, isEditMode, selectedMetric]);


    


    
    useEffect(() => {
        if (selectedMetric) return;
    
        if (!metadata) return;
        if (!base_granularity || !category) return;
    
        const match = metadata.agg_functions?.find(
        (fn) =>
            fn.from_granularity === base_granularity &&
            fn.category.toLowerCase() === category.toLowerCase()
        );
    
        if (match) {
        setagg_func_id(match.agg_func_id);
        }
    }, [base_granularity, category, metadata, selectedMetric]);
      


    const [errors, setErrors] = useState({
        metric_name: false,
    });

    // validating reuqired fields
    const validateFields = () => {
        const newErrors = {
          metric_name: !metric_name,
        };
    
        setErrors(newErrors);
        return !Object.values(newErrors).some((error) => error);
    };

    const resetForm = () => {
        setIso_code('');
        setmetric_name('');
        setdescription('');
        setunits('');
        settime_to_live(5400);
        setcategory('');
        setaward_type('');
        setmarket_type('');
        setbase_granularity('');
        setfuel_type('');
        setmetric_internal_key('');
        setbase_entity_name('');
        setsource([]);
        setVendorMappings([]);
        setOriginalVendorMappings([]);
        setOriginalSources([]);
        setagg_func_id('');
        setproduct('');
        setsettle_charge_code('');
        setsettle_charge_name('');
        setIsEditMode(false);
        setIsViewMode(false);
        onDirtyChange(false); 
        // onModeChange(null);
        setModifiedFields({});
    };


    const handleNew = () => {
        setDialog({
        open: true,
        title: METRIC_TAB.DIALOGS.CONFIRM_NEW_TITLE,
        message: METRIC_TAB.DIALOGS.CONFIRM_NEW_MESSAGE,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
            closeDialog();
            resetForm();
            setSelectedMetric(null);
            onDirtyChange(false);
            setIsEditMode(false);
            onClearSelection();
        },
        onCancel: closeDialog,
        });
    };

    
    const originalMetricRef = React.useRef<any>(null);

    const handleEdit = () => {
        // Save original form state before editing
        originalMetricRef.current = {
            iso_code,
            metric_name,
            description,
            units,
            time_to_live,
            category,
            award_type,
            market_type,
            base_granularity,
            fuel_type,
            metric_internal_key,
            pi_metric_internal_key,
            base_entity_name,
            source: [...source],
            agg_func_id,
            product,
            settle_charge_code,
            settle_charge_name,
            status,
            vendorMappings: vendorMappings.map((mapping) => ({ ...mapping })),
        };
    
        setIsViewMode(false);
        setIsEditMode(true);
        onDirtyChange(true);
        setModifiedFields({});
    };
  

    const handleDeleteClick = () => {
        const rows: any[] = Array.isArray(selectedMetric)
            ? selectedMetric
            : selectedMetric
                ? [selectedMetric]
                : [];

        const publishedRow = rows.find((m: any) => m.status !== "Internal");

        // Deleting a published metric is not allowed, and this has to be checked
        // before anything is sent. The API deletes one source row per call and
        // only refuses the last one, so a multi-source metric would otherwise
        // lose its first source before the final delete was rejected.
        if (publishedRow) {
            setDialog({
                open: true,
                title: "Cannot Delete Metric",
                message: `This metric has status "${publishedRow.status}" and cannot be deleted. Only metrics with status "Internal" can be deleted.`,
                confirmButtonText: "Close",
                onConfirm: closeDialog,
            });
            return;
        }

        setDialog({
        open: true,
        title: "Delete Metric?",
        message:
            rows.length === 2
            ? "This metric exists for multiple sources (Snowflake & PI). Do you want to delete ALL metric entries?"
            : "Are you sure you want to delete this metric?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: async () => {
            closeDialog();

            for (const m of rows) {
            // stop at the first failure rather than pressing on and leaving
            // the metric partly deleted
            if (!(await onDeleteMetric(m.metric_id))) return;
            }

            resetForm();
            setSelectedMetric(null);
            onDirtyChange(false);
        },
        onCancel: closeDialog,
        });
    };

    const handleSaveEditedMetric = async () => {
        if (!selectedMetric || !Array.isArray(selectedMetric)) return;

        const nautilusItem = selectedMetric.find(m => m.source === "nautilus");
        const piItem = selectedMetric.find(m => m.source === "pi");

        const wantsNautilus = source.includes("nautilus");
        const wantsPi = source.includes("pi");

        const sharedFields = {
        iso_code,
        status,
        metric_name,
        description,
        units,
        time_to_live: Number(time_to_live) || 5400,
        category,
        award_type,
        market_type,
        base_granularity,
        fuel_type,
        product,
        settle_charge_code,
        settle_charge_name,
        };

        // Status ALL freezes name and description, so leave them out of the PUT
        // entirely. This matters because the two rows of a dual-source metric can
        // hold different descriptions while the form only shows the Snowflake
        // one — sending it would be a frozen-field change on the PI row.
        const forUpdate = (fields: Record<string, any>, metricId: number) => {
        const payload: Record<string, any> = {
            metric_id: metricId,
            ...fields,
            sys_modified_by: modifiedBy,
        };
        if (isStatusAll) {
            delete payload.metric_name;
            delete payload.description;
        }
        return payload;
        };

        // A create must always carry the name — it is a required field.
        const forCreate = (fields: Record<string, any>) => ({
        ...fields,
        sys_created_by: createdBy,
        sys_modified_by: modifiedBy,
        });

        const snowflakeFields = {
        ...sharedFields,
        command_type: getCommandTypeFromSource("nautilus"),
        metric_internal_key,
        base_entity_name,
        source: "nautilus",
        agg_func_id: agg_func_id ? Number(agg_func_id) : null,
        };

        const piFields = {
        ...sharedFields,
        command_type: getCommandTypeFromSource("pi"),
        metric_internal_key: pi_metric_internal_key || null,
        base_entity_name: null,
        source: "pi",
        agg_func_id: null,
        };

        // A save can touch both source rows. Collect what happened and report it
        // in a single dialog rather than one per request.
        const created: string[] = [];
        const updated: string[] = [];

        try {
        // SNOWFLAKE — update the existing row, or create it if the source was
        // just added to this metric.
        if (wantsNautilus) {
            const ok = nautilusItem
                ? await onSaveChanges(
                    forUpdate(snowflakeFields, nautilusItem.metric_id),
                    { silent: true }
                  )
                : await onSaveMetric(forCreate(snowflakeFields), { silent: true });

            // the failure is already surfaced by the caller's error dialog
            if (!ok) return;
            (nautilusItem ? updated : created).push(SOURCE_LABELS.nautilus);
        }

        // PI — same: PUT when the row already exists, POST when newly added.
        if (wantsPi) {
            const ok = piItem
                ? await onSaveChanges(forUpdate(piFields, piItem.metric_id), {
                    silent: true,
                  })
                : await onSaveMetric(forCreate(piFields), { silent: true });

            if (!ok) return;
            (piItem ? updated : created).push(SOURCE_LABELS.pi);
        }

        // Un-ticked sources are deleted last, so a source being added in the same
        // save already exists by the time the old row goes away. The API refuses
        // to delete the final row of a published metric.
        const removed: string[] = [];

        for (const src of pendingSourceRemovals) {
            const row = selectedMetric.find((m: any) => m.source === src);
            if (!row) continue;

            if (!(await onDeleteMetric(row.metric_id))) return;
            removed.push(SOURCE_LABELS[src] || src);
        }

        const primaryMetric = nautilusItem || piItem || selectedMetric[0];
        if (primaryMetric?.metric_id && hasVendorMappingChanges()) {
            await saveVendorMappings(primaryMetric.metric_id);
        }

        // nothing selected and nothing removed → nothing was sent
        if (!created.length && !updated.length && !removed.length) return;

        resetForm();
        setSelectedMetric(null);
        onSaveSuccess(describeSave(metric_name, created, updated, removed));
        } catch (err) {
        handleApiError(err, "Failed to save metric(s)");
        }
    };

    
    const handleCancelClick = () => {
        setDialog({
            open: true,
            title: "Cancel Editing?",
            message: "Are you sure you want to discard changes?",
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            onConfirm: () => {
                closeDialog();
    
                const original = originalMetricRef.current;
                if (original) {
                    setIso_code(original.iso_code);
                    setmetric_name(original.metric_name);
                    setdescription(original.description);
                    setunits(original.units);
                    settime_to_live(original.time_to_live);
                    setcategory(original.category);
                    setaward_type(original.award_type);
                    setmarket_type(original.market_type);
                    setbase_granularity(original.base_granularity);
                    setfuel_type(original.fuel_type);
                    setmetric_internal_key(original.metric_internal_key);
                    setPiMetricInternalKey(original.pi_metric_internal_key);
                    setbase_entity_name(original.base_entity_name);
                    setsource(original.source);
                    setagg_func_id(original.agg_func_id);
                    setproduct(original.product);
                    setsettle_charge_code(original.settle_charge_code);
                    setsettle_charge_name(original.settle_charge_name);
                    setStatus(original.status);
                    setVendorMappings(original.vendorMappings || []);
                }
    
                setModifiedFields({});
                onDirtyChange(false);
                setIsEditMode(false);
                setIsViewMode(true);
            },
            onCancel: closeDialog,
        });
    };
  

    const handleSaveNewMetric = async () => {
        const isValid = validateFields();
        if (!isValid) {
        setDialog({
            open: true,
            title: METRIC_TAB.VALIDATION.MISSING_INFO_TITLE,
            message: METRIC_TAB.VALIDATION.MISSING_INFO_MESSAGE,
            confirmButtonText: "Close",
            onConfirm: closeDialog,
        });
        return;
        }
    
        // Create metric payload BASE —
        const snowflakeMetric = {
          iso_code,
          metric_name,
          description,
          units,
          time_to_live,
          category,
          award_type,
          market_type,
          base_granularity,
          fuel_type,
          command_type: getCommandTypeFromSource("nautilus"),
          metric_internal_key,   // SNOWFLAKE KEY
          base_entity_name,
          source: "nautilus",
          agg_func_id: agg_func_id ? Number(agg_func_id) : null,
          product,
          settle_charge_code,
          settle_charge_name,
        sys_created_by: createdBy,
        sys_modified_by: modifiedBy,
        };
    
        // PI metric payload (SECOND request)
        const piMetric = {
          iso_code,
          metric_name,
          description,
          units,
          time_to_live,
          category,
          award_type,
          market_type,
          base_granularity,
          fuel_type,
          command_type: getCommandTypeFromSource("pi"),
          metric_internal_key: pi_metric_internal_key || null,
          base_entity_name: null,
          source: "pi",
          agg_func_id: null,
          product,
          settle_charge_code,
          settle_charge_name,
        sys_created_by: createdBy,
        sys_modified_by: modifiedBy,
        };
    
        // One POST per selected source, reported as a single dialog at the end.
        const created: string[] = [];
        let vendorMappingMetricId: number | undefined;

        try {
        if (source.includes("nautilus")) {
            const createdMetric = await onSaveMetric(snowflakeMetric, { silent: true });
            if (!createdMetric) return;
            vendorMappingMetricId = createdMetric.metric_id;
            created.push(SOURCE_LABELS.nautilus);
        }

        if (source.includes("pi")) {
            const createdMetric = await onSaveMetric(piMetric, { silent: true });
            if (!createdMetric) return;
            vendorMappingMetricId ||= createdMetric.metric_id;
            created.push(SOURCE_LABELS.pi);
        }

        if (!created.length) return;

        if (vendorMappings.length > 0 && vendorMappingMetricId) {
            await saveVendorMappings(vendorMappingMetricId);
        }

        resetForm();
        onDirtyChange(false);
        onSaveSuccess(describeSave(metric_name, created, []));
        } catch (error) {
        handleApiError(error, "Failed to create metric(s)");
        }
    };


    const renderViewModeButtons = () => (
        <>
        <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-gray-600 transition-colors duration-300 shadow-md transform hover:scale-105"
        >
            <FaPlus className="mr-2" /> New
        </button>
    
        <button
            onClick={handleEdit}
            className={`flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-gray-600 transition-colors duration-300 shadow-md transform hover:scale-105 ${
            isEditMode ? "opacity-50 cursor-not-allowed" : ""
            }`}
        >
            <FaEdit className="mr-2" /> Edit
        </button>
        </>
    );

    const renderEditModeButtons = () => (
        <>
        
        {canDelete && (
        <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isStatusAll}
            title={
                isStatusAll
                    ? "Metrics with status ALL cannot be deleted"
                    : undefined
            }
            className={`flex items-center gap-2 px-4 py-2 font-medium rounded-lg ${
                isStatusAll
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-500 text-white hover:bg-red-600"
            }`}
        >
            <FaTrash className="w-4 h-4" /> Delete
        </button>
        )}
    
        <div className="flex-1" />
    
        <button
            onClick={() => {
            setDialog({
                open: true,
                title: METRIC_TAB.DIALOGS.CONFIRM_EDIT_TITLE,
                message: pendingSourceRemovals.length > 0 ? (
                    <div className="space-y-2 text-left">
                        <p>{METRIC_TAB.DIALOGS.CONFIRM_EDIT_MESSAGE}</p>
                        <p className="text-red-600">
                            This will also DELETE the{" "}
                            {pendingSourceRemovals
                                .map(src => SOURCE_LABELS[src] || src)
                                .join(" and ")}{" "}
                            {pendingSourceRemovals.length > 1 ? "entries" : "entry"} for
                            this metric.
                        </p>
                    </div>
                ) : METRIC_TAB.DIALOGS.CONFIRM_EDIT_MESSAGE,
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                onConfirm: () => {
                closeDialog();
                handleSaveEditedMetric();
                setModifiedFields({});
                },
                onCancel: closeDialog,
            });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-gray-600 transition-colors duration-300 shadow-md transform hover:scale-105"
        >
            <FaSave className="w-4 h-4" /> Save
        </button>
    
        <button
            onClick={handleCancelClick}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-gray-600 transition-colors duration-300 shadow-md transform hover:scale-105"
        >
            Cancel
        </button>
        </>
    );
  
    
    const renderCreateButton = () => {
        const isDirty = Object.keys(modifiedFields).length > 0;
    
        return (
        <>
            {/* CLEAR — only when creating & dirty */}
            {isDirty && (
            <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors duration-300 shadow-md"
            >
                Clear
            </button>
            )}
    
            {/* CREATE */}
            <button
            onClick={() => {
                const isValid = validateFields();
                if (!isValid) {
                setDialog({
                    open: true,
                    title: METRIC_TAB.VALIDATION.MISSING_INFO_TITLE,
                    message: METRIC_TAB.VALIDATION.MISSING_INFO_MESSAGE,
                    confirmButtonText: "Close",
                    onConfirm: closeDialog,
                });
                return;
                }
    
                setDialog({
                open: true,
                title: METRIC_TAB.DIALOGS.CONFIRM_CREATE_TITLE,
                message: METRIC_TAB.DIALOGS.CONFIRM_CREATE_MESSAGE,
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                onConfirm: () => {
                    closeDialog();
                    handleSaveNewMetric();
                },
                onCancel: closeDialog,
                });
            }}
            className="flex items-center gap-2 w-auto px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300 shadow-md"
            >
            <FaPlus className="w-4 h-4" /> Create
            </button>
        </>
        );
    };

    const handleClear = () => {
        resetForm();               // reset all inputs
        setSelectedMetric(null);   // ensure create state
        onDirtyChange(false);      // clear dirty flag
        onModeChange?.(null);      // exit create mode cleanly
    };
      
      


    const baseEntityMap: Record<string, string> = {
        "15minute": "power.vw_fact_fifteen_mins",
        "5minute": "power.vw_fact_five_mins",
        "day": "power.vw_fact_daily_data",
        "hour": "power.vw_fact_hourly",
    };


    

    // Toggling a source only changes the form. Adds and removals are both applied
    // on Save, so the selection stays reversible until then.
    const handleSourceChange = (updatedSources: string[]) => {
        // A metric must keep at least one source; removing them all is what the
        // Delete button is for.
        if (selectedMetric && updatedSources.length === 0) {
            setDialog({
                open: true,
                title: "Source Required",
                message:
                    "A metric must have at least one source. Use Delete if you want to remove the metric entirely.",
                confirmButtonText: "Close",
                onConfirm: closeDialog,
            });
            return;
        }

        setsource(updatedSources);
        onDirtyChange(true);
        setModifiedFields(prev => ({ ...prev, source: true }));
    };

    const handleVendorSourceChange = (selectedIds: number[]) => {
        setVendorMappings((previousMappings) =>
            selectedIds.map((vendorSourceId) =>
                previousMappings.find((mapping) => mapping.vendor_source_id === vendorSourceId) || {
                    vendor_source_id: vendorSourceId,
                    vendor_table: "",
                    data_collection_frequency: "",
                }
            )
        );
        onDirtyChange(true);
    };

    const updateVendorMapping = (
        vendorSourceId: number,
        field: "vendor_table" | "data_collection_frequency",
        value: string
    ) => {
        setVendorMappings((previousMappings) => previousMappings.map((mapping) =>
            mapping.vendor_source_id === vendorSourceId
                ? { ...mapping, [field]: value }
                : mapping
        ));
        onDirtyChange(true);
    };

    const hasVendorMappingChanges = () => {
        const normalize = (mappings: VendorMapping[]) => mappings
            .map((mapping) => ({
                ...mapping,
                vendor_table: mapping.vendor_table.trim(),
            }))
            .sort((first, second) => first.vendor_source_id - second.vendor_source_id);

        return JSON.stringify(normalize(vendorMappings)) !== JSON.stringify(normalize(originalVendorMappings));
    };

    const saveVendorMappings = async (metricId: number) => {
        const headers = await getAssetHeaders();
        await apiClientAdmin.put(
            `/ref-data/metric-config/${metricId}/vendor-sources`,
            { vendor_sources: vendorMappings },
            { headers }
        );
    };

    const vendorSourceLabel = (vendorSource?: VendorSource) =>
        vendorSource?.vendor_source_name === "Snowflake"
            ? "YES Energy"
            : vendorSource?.vendor_source_name || "";


    const hasSnowflakeSource = source.includes("nautilus");
    const hasPiSource = source.includes("pi");

    // When Snowflake is added to a metric that never had it, the Snowflake-only
    // fields are empty. Derive them the same way the create flow does.
    useEffect(() => {
        if (!selectedMetric || !isEditMode) return;
        if (!hasSnowflakeSource) return;

        // only for a source that has no row yet — never touch a saved one
        const hasSnowflakeRow = (Array.isArray(selectedMetric) ? selectedMetric : [selectedMetric])
            .some((m: any) => m.source === "nautilus");
        if (hasSnowflakeRow) return;

        if (!base_entity_name && base_granularity && baseEntityMap[base_granularity]) {
            setbase_entity_name(baseEntityMap[base_granularity]);
        }

        if (!agg_func_id && metadata && base_granularity && category) {
            const match = metadata.agg_functions?.find(
                (fn) =>
                    fn.from_granularity === base_granularity &&
                    fn.category?.toLowerCase() === category.toLowerCase()
            );
            if (match) setagg_func_id(match.agg_func_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedMetric,
        isEditMode,
        hasSnowflakeSource,
        base_granularity,
        category,
        metadata,
        base_entity_name,
        agg_func_id,
    ]);

    const getCommandTypeFromSource = (src: "nautilus" | "pi") => {
        switch (src) {
        case "nautilus":
            return "SQL";
        case "pi":
            return "API";
        default:
            return null;
        }
    };
      


    const [showAggDialog, setShowAggDialog] = useState(false);
    const [selectedAggRows, setSelectedAggRows] = useState<any[]>([]);

    const openAggDialog = () => {
        if (!metadata) {
        setSelectedAggRows([]);
        setShowAggDialog(true);
        return;
        }
    
        const aggId = Number(agg_func_id);
    
        // Get ALL rows matching AGG_FUNC_ID
        let matches = metadata.agg_functions?.filter(
        (fn: any) => fn.agg_func_id === aggId
        );
    
        // If none matched by ID → fallback (may return multiple rows)
        if (!matches || matches.length === 0) {
        matches = metadata.agg_functions?.filter(
            (fn: any) =>
            fn.from_granularity === base_granularity &&
            fn.category?.toLowerCase() === category?.toLowerCase()
        );
        }
    
        setSelectedAggRows(matches || []);
        setShowAggDialog(true);
    };

      // Inline Dialog Component (ONLY for Agg Function Details)
    const AggDetailsDialog = ({
        title,
        message,
        onConfirm,
    }: {
        title: string;
        message: React.ReactNode;
        onConfirm: () => void;
    }) => {
        return (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold text-center mb-4">{title}</h3>
    
            <div className="text-gray-700 text-center mb-6">{message}</div>
    
            <div className="flex justify-center">
                <button
                onClick={onConfirm}
                className="px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
                >
                OK
                </button>
            </div>
            </div>
        </div>
        );
    };

    const closeAggDialog = () => {
        setShowAggDialog(false);
        setSelectedAggRows([]);
    };
      
    
    const handleStatusChange = (newStatus: string) => {
      // Block ALL → Internal
      if (status === "ALL" && newStatus !== "ALL") {
          setDialog({
              open: true,
              title: "Action Not Allowed",
              message:
                  "Metrics with status ALL cannot be changed back to any other status.",
              confirmButtonText: "Close",
              onConfirm: closeDialog,
          });
          return;
      }
  
      // Confirm Internal → ALL
      if (status === "Internal" && newStatus === "ALL") {
          setDialog({
              open: true,
              title: "Change Status to ALL?",
              message: (
                  <div className="space-y-2 text-left">
                      <p>
                          You are about to change the metric status to <b>ALL</b>.
                      </p>
                      <ul className="list-disc pl-5 text-sm text-gray-700">
                          <li>Once set to ALL, the status cannot be changed back.</li>
                          <li>Metrics with ALL status cannot be deleted.</li>
                      </ul>
                      <p className="mt-2">
                          Do you want to continue?
                      </p>
                  </div>
              ),
              confirmButtonText: "Yes, Change to ALL",
              cancelButtonText: "Cancel",
              onConfirm: () => {
                  closeDialog();
                  setStatus("ALL");
                  onDirtyChange(true);
                  setModifiedFields(prev => ({ ...prev, status: true }));
              },
              onCancel: closeDialog,
          });
          return;
      }
  
      // Normal change (e.g., Internal → Internal)
      setStatus(newStatus);
      onDirtyChange(true);
      setModifiedFields(prev => ({ ...prev, status: true }));
  };
  
      

    return (
        <form onSubmit={(e) => e.preventDefault()} className="h-full flex flex-col font-poppins">
        <div className="bg-white rounded-lg shadow-lg h-full flex flex-col">
            {dialog.open && (
            <Dialog
            title={dialog.title}
            message={dialog.message}
            confirmButtonText={dialog.confirmButtonText}
            cancelButtonText={dialog.cancelButtonText}
            onConfirm={dialog.onConfirm}
            onCancel={dialog.onCancel}
            onClose={closeDialog} 
            />
            )}

            {/* Header Buttons (none without admin, so no empty bar either) */}
            {canDelete && (
            <div className="px-8 pt-6 pb-4 border-b">
            <div className="flex justify-end gap-3">
                {selectedMetric
                ? isEditMode
                    ? renderEditModeButtons()
                    : renderViewModeButtons()
                : renderCreateButton()}
            </div>
            </div>
            )}

            {/* Form Rows */}
            <div className="flex-1 overflow-y-auto px-8 py-6">

            {/* First Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Metric Name */}
                <div>
                    <label className="block text-gray-600 mb-2">
                    Metric Name
                    <span className="text-red-500 text-lg align-top ml-1">*</span>
                    {isStatusAll && (
                        <span className="ml-2 text-xs text-gray-500">
                            (locked — status is ALL)
                        </span>
                    )}
                    </label>

                    <input
                    type="text"
                    value={metric_name}
                    onChange={(e) => {
                        const value = e.target.value;
                        setmetric_name(value);
                        onDirtyChange(true);
                        setModifiedFields(prev => ({ ...prev, metric_name: true }));
                    }}
                    placeholder="Metric Name"
                    disabled={fieldsLocked || isStatusAll}
                    onFocus={() => setActiveField("metric_name")}
                    className={`w-full px-3 py-3.5 mt-[-2px] border rounded-lg shadow-inner text-sm transition-all duration-200
                        ${
                        errors.metric_name && !metric_name
                            ? "border-red-500 focus:ring-red-500"
                            : !isEditMode || isStatusAll
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && (activeField === "metric_name" || modifiedFields.metric_name)
                                ? "border-green-500 focus:ring-2 focus:ring-green-500"
                                : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                        }`}
                    />
                </div>

                {/* Status */}
                {selectedMetric && (
                    <div>
                    <label className="block text-gray-600 mb-2">
                        Status
                        <span className="text-red-500 text-lg align-top ml-1">*</span>
                    </label>

                    <StatusDropdown
                        value={status}
                        onChange={handleStatusChange}
                        disabled={fieldsLocked}
                        onFocus={() => setActiveField("status")}
                        className={`w-full p-1 mt-[-2px] border rounded-lg shadow-inner text-sm transition-all duration-200
                        ${
                            !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && modifiedFields.status
                                ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white"
                                : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                        }`}
                    />
                    </div>
                )}
            </div>


            {/* Second Row */}
            <div className="mt-4">
                <label className="block text-gray-600 mb-1">
                    Description
                    {/* <span className="text-red-500 text-lg align-top ml-1">*</span> */}
                    {isStatusAll && (
                        <span className="ml-2 text-xs text-gray-500">
                            (locked — status is ALL)
                        </span>
                    )}
                </label>
                <textarea
                    value={description}
                    placeholder="Enter description..."
                    onChange={(e) => {
                    const value = e.target.value;
                    setdescription(value);
                    onDirtyChange(true);
                    setModifiedFields(prev => ({ ...prev, description: true }));
                    }}
                    onFocus={() => setActiveField("description")}
                    disabled={fieldsLocked || isStatusAll}
                    rows={5}
                    className={`w-full p-3 border rounded-lg shadow-inner text-sm transition-all duration-200 resize-none
                    ${
                        !isEditMode || isStatusAll
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && (activeField === "description" || modifiedFields.description)
                                ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}/>
            </div>

            {/* Third Row */}
            <div className="p-6 bg-[#f9f9f9] border border-gray-300 rounded-lg shadow-sm mt-5">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="flex flex-col">
                        <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">
                            ISO Code
                        </label>
                        <MetricGroupISODropdown
                            selectedISO={iso_code}
                            onISOChange={(iso) => {
                                setIso_code(iso);
                                onDirtyChange(true);
                                setModifiedFields(prev => ({ ...prev, iso_code: true }));
                            }}
                            onFocus={() => setActiveField("iso_code")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200
                            ${
                                !isEditMode
                                    ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                    : isEditMode && (activeField === "iso_code" || modifiedFields.iso_code)
                                        ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                        : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>


                    <div className="flex flex-col">
                        <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">Fuel Type</label>
                        <FuelTypeDropdown
                            selectedFuelType={fuel_type}
                            onFuelTypeChange={(value) => {
                                setfuel_type(value);
                                onDirtyChange(true);
                                setModifiedFields(prev => ({ ...prev, fuel_type: true }));
                            }}
                            onFocus={() => setActiveField("fuel_type")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 
                            ${
                                !isEditMode
                                    ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                    : isEditMode && (activeField === "fuel_type"|| modifiedFields.fuel_type)
                                        ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                        : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">Category</label>
                        <CategoryDropdown
                            selectedCategory={category}
                            onCategoryChange={(value) => {
                            setcategory(value);
                            onDirtyChange(true); 
                            setModifiedFields(prev => ({ ...prev, category: true }));
                            }}
                            onFocus={() => setActiveField("category")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 
                            ${
                                !isEditMode
                                    ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                    : isEditMode && (activeField === "category"|| modifiedFields.category)
                                        ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                        : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>
                </div>
            </div>

            {/* Fourth and Fith Row Group */}
            <div className="p-6 bg-[#f9f9f9] border border-gray-300 rounded-lg shadow-sm mt-5">

                {/* Fourth Row */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="flex flex-col">
                        <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">
                            Market Type
                        </label>

                        <MarketTypeDropdown
                            selectedMarketType={market_type}
                            onMarketTypeChange={(value) => {
                            setmarket_type(value);
                            onDirtyChange(true);
                            setModifiedFields((prev) => ({ ...prev, market_type: true }));
                            }}
                            isoCode={iso_code} // TIED TO ISO VALUE
                            onFocus={() => setActiveField("market_type")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 
                            ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "market_type" || modifiedFields.market_type)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">
                            Award Type
                        </label>
                        <AwardTypeDropdown
                            metadata={metadata}
                            selectedAwardType={award_type}
                            onAwardTypeChange={(value) => {
                                setaward_type(value);
                                onDirtyChange(true);
                                setModifiedFields(prev => ({ ...prev, award_type: true }));
                            }}
                            onFocus={() => setActiveField("award_type")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 
                            ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "award_type" || modifiedFields.award_type)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">
                            Product Type
                        </label>
                        <ProductTypeDropdown
                            metadata={metadata}
                            selectedProductType={product}
                            onProductTypeChange={(value) => {
                                setproduct(value);
                                onDirtyChange(true);
                                setModifiedFields(prev => ({ ...prev, product: true }));
                            }}
                            onFocus={() => setActiveField("product")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 
                            ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "product" || modifiedFields.product)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>
                </div>

                {/* Fifth Row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
                    <div className="sm:col-span-1">
                        <label className="block text-gray-600 mb-1">
                            Settlement Charge Code
                            {/* <span className="text-red-500 text-lg align-top ml-1">*</span> */}
                        </label>
                        <input
                            type="text"
                            value={settle_charge_code}
                            onChange={(e) => {
                            const value = e.target.value;
                            setsettle_charge_code(value);
                            onDirtyChange(true);
                            setModifiedFields(prev => ({ ...prev, settle_charge_code: true }));
                            }}
                            placeholder="Code"
                            disabled={fieldsLocked}
                            onFocus={() => setActiveField("settle_charge_code")}
                            className={`w-full p-3 border rounded-lg shadow-inner text-sm transition-all duration-200
                            ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "settle_charge_code" || modifiedFields.settle_charge_code)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}                           
                        />
                    </div>

                    <div className="sm:col-span-2">
                        <label className="block text-gray-600 mb-1">
                            Settlement Charge Code Long Name
                            {/* <span className="text-red-500 text-lg align-top ml-1">*</span> */}
                        </label>
                        <input
                            type="text"
                            value={settle_charge_name}
                            onChange={(e) => {
                                const value = e.target.value;
                                setsettle_charge_name(value);
                                onDirtyChange(true);
                                setModifiedFields(prev => ({
                                    ...prev,
                                    settle_charge_name: true
                                }));
                            }}
                            placeholder="Long Name"
                            disabled={fieldsLocked}
                            onFocus={() => setActiveField("settle_charge_name")}
                            className={`w-full p-3 border rounded-lg shadow-inner text-sm transition-all duration-200
                            ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "settle_charge_name" || modifiedFields.settle_charge_name)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                        />
                    </div>
                </div>

            </div>

                <section className="mt-5 p-6 bg-[#f9f9f9] border border-gray-300 rounded-lg shadow-sm">
                <h3 className="text-gray-800 text-lg font-semibold mb-4">AMP DATA Mapping</h3>
                <div className="rounded-lg border border-gray-200 bg-white p-5">
                    <label className="block text-gray-600 mb-2">Vendor Source</label>
                    <VendorSourceMultiSelect
                        options={vendorSources}
                        selectedIds={vendorMappings.map((mapping) => mapping.vendor_source_id)}
                        onChange={handleVendorSourceChange}
                        disabled={fieldsLocked}
                        getLabel={vendorSourceLabel}
                    />

                    {vendorMappings.length > 0 && (
                        <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200 bg-white">
                            <table className="w-full min-w-[680px] border-collapse">
                                <thead className="border-b border-gray-200 bg-gray-50 text-left text-sm font-medium text-gray-600">
                                    <tr>
                                        <th className="px-4 py-3">Vendor Source</th>
                                        <th className="px-4 py-3">Vendor Table</th>
                                        <th className="px-4 py-3">Data Collection Frequency</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {vendorMappings.map((mapping) => {
                                    const vendorSource = vendorSources.find(
                                        (item) => item.vendor_source_id === mapping.vendor_source_id
                                    );
                                    return (
                                        <tr key={mapping.vendor_source_id}>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-800">
                                                {vendorSourceLabel(vendorSource) || mapping.vendor_source_id}
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                type="text"
                                                value={mapping.vendor_table}
                                                onChange={(event) => updateVendorMapping(mapping.vendor_source_id, "vendor_table", event.target.value)}
                                                placeholder="Enter vendor table"
                                                disabled={fieldsLocked}
                                                className={`w-full p-2 border rounded-lg shadow-inner text-sm ${
                                                    !isEditMode
                                                        ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                                        : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                                                }`}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                value={mapping.data_collection_frequency}
                                                onChange={(event) => updateVendorMapping(mapping.vendor_source_id, "data_collection_frequency", event.target.value)}
                                                disabled={fieldsLocked}
                                                className={`w-full p-2 border rounded-lg shadow-inner text-sm ${
                                                    !isEditMode
                                                        ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                                        : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                                                }`}
                                            >
                                                <option value="">Select frequency</option>
                                                {frequencies.map((frequency) => (
                                                    <option key={frequency.code} value={frequency.code}>{frequency.label}</option>
                                                ))}
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div>

            {/* Sixth Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                    <label className="block text-gray-600 mb-1">
                    Base Granularity<span className="text-red-500 text-lg align-top ml-1">*</span>
                    </label>
                    <BaseGranularityDropdown
                        selectedBaseGranularity={base_granularity}
                        onBaseGranularityChange={(value) => {
                            setbase_granularity(value);
                            onDirtyChange(true);
                            setModifiedFields(prev => ({ ...prev, base_granularity: true }));
                    
                            // Auto-fill Base Entity
                            const mappedEntity = baseEntityMap[value];
                            if (mappedEntity) {
                                setbase_entity_name(mappedEntity);
                                setModifiedFields(prev => ({ ...prev, base_entity_name: true }));
                            }
                        }}
                        onFocus={() => setActiveField("base_granularity")}
                        disabled={fieldsLocked}
                        className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                        ${
                        !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && (activeField === "base_granularity" || modifiedFields.base_granularity)
                                ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                        }`}
                    />
                </div>

                <div>
                    <label className="block text-gray-600 mb-1">
                    Units<span className="text-red-500 text-lg align-top ml-1">*</span>
                    </label>
                    <UnitsDropdown
                        selectedUnits={units}
                        onUnitsChange={(value) => {
                            setunits(value);
                            onDirtyChange(true);
                            setModifiedFields(prev => ({ ...prev, units: true }));
                        }}
                        onFocus={() => setActiveField("units")}
                        disabled={fieldsLocked}
                        className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                        ${
                        !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && (activeField === "units" || modifiedFields.units)
                                ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                        }`}
                    />
                </div>
            </div>

            {/* Seventh Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                <div>
                    <label className="block text-gray-600 mb-1">
                    Source<span className="text-red-500 text-lg align-top ml-1">*</span>
                    </label>
                    <SourceDropdown
                    selectedSources={source}
                    onSourceChange={handleSourceChange}
                    onFocus={() => setActiveField("source")}
                    disabled={fieldsLocked}
                    className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                    ${
                    !isEditMode
                        ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                        : isEditMode &&(activeField === "source" || modifiedFields.source)
                            ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
                    />
                </div>
            </div>

            {/* SNOWFLAKE SECTION — shown if user selected 'snowflake' */}
            {hasSnowflakeSource  && (
                <div className="p-6 bg-[#f9f9f9] border border-gray-300 rounded-lg shadow-sm mt-5">
                    <div className="mb-4">
                        <span className="text-gray-800 text-lg font-semibold">
                            Command Type : <span className="text-[#ef4444] font-bold">SQL</span>
                        </span>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {/* Base Entity Name (Readonly) */}
                        <div className="flex flex-col">
                            <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">
                            Table Name
                            </label>
                            <BaseEntityDropdown
                            selectedBaseEntity={base_entity_name}
                            onBaseEntityChange={() => {}}
                            onFocus={() => setActiveField("base_entity_name")}
                            disabled={!canDelete}
                            className={`w-full p-1 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                                pointer-events-none
                                bg-[#f9f9f9] text-gray-800 border-gray-300
                            `}
                            />
                        </div>

                        {/* Metric Internal Key */}
                        <div className="flex flex-col">
                            <label className="block text-gray-600 text-sm mb-2 truncate min-h-[20px]">
                            Column Name
                            </label>

                            <MetricInternalKeyDropdown
                            baseEntityName={base_entity_name}
                            source={"snowflake"} 
                            selectedMetricKey={metric_internal_key}
                            onMetricKeyChange={(value) => {
                                setmetric_internal_key(value);
                                onDirtyChange(true);
                                setModifiedFields(prev => ({ ...prev, metric_internal_key: true }));
                            }}
                            onFocus={() => setActiveField("metric_internal_key")}
                            disabled={fieldsLocked}
                            className={`w-full p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "metric_internal_key" || modifiedFields.metric_internal_key)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                            }`}
                            />
                        </div>

                    </div>
                </div>
            )}

            {/* PI SECTION — shown if user selected 'pi' */}
            {hasPiSource  && (
                <div className="p-6 bg-[#f9f9f9] border border-gray-300 rounded-lg shadow-sm mt-5">
                    <div className="flex items-center space-x-3">
                    <span className="text-gray-800 text-lg font-semibold">
                        Command Type : <span className="text-[#ef4444] font-bold">PI</span>
                    </span>
                    <input
                        type="checkbox"
                        checked={true}
                        readOnly
                        className="w-5 h-5 accent-[#ef4444] cursor-not-allowed"
                    />
                    </div>
                    {/* INPUT FOR PI METRIC INTERNAL KEY */}
                    <div className="mt-4">
                    <label className="block text-gray-600 text-sm mb-2">PI Metric Internal Key</label>
                    <input
                        type="text"
                        value={pi_metric_internal_key}
                        onChange={(e) => {
                        setPiMetricInternalKey(e.target.value);
                        onDirtyChange(true);
                        setModifiedFields(prev => ({ ...prev, pi_metric_internal_key: true }));
                        }}
                        onFocus={() => setActiveField("pi_metric_internal_key")}
                        disabled={fieldsLocked}
                        className={`w-full p-3 border rounded-lg shadow-inner text-sm 
                        ${
                        !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && (activeField === "pi_metric_internal_key" || modifiedFields.pi_metric_internal_key)
                                ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                        }`}                          
                        placeholder="Enter PI metric internal key"
                    />
                    </div>
                </div>
            )}

            {/* Eigth Row */}
            <div className="mt-5">

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-gray-600 mb-1">Time to Live (in seconds)</label>
                        <input
                            type="text"
                            value={time_to_live}
                            onChange={(e) => {
                                const value = e.target.value;
                                settime_to_live(value === "" ? 5400 : Number(value));
                                onDirtyChange(true);
                                setModifiedFields(prev => ({ ...prev, time_to_live: true }));
                              }}
                            onFocus={() => setActiveField("timeToLive")}
                            placeholder="Enter Time to Live || Default(5400)"
                            disabled={fieldsLocked}
                            className={`w-full p-3 pl-4 pr-10 border rounded-lg text-sm transition-all ease-in-out duration-300 shadow-inner appearance-none
                            ${
                            !isEditMode
                                ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                                : isEditMode && (activeField === "time_to_live" || modifiedFields.time_to_live)
                                    ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                    : "border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white text-black"
                            }`}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-600 mb-1 flex justify-between items-center">
                            <span>Agg Function</span>

                            <button
                            type="button"
                            onClick={openAggDialog}
                            className="text-sm underline text-blue-600 hover:text-blue-800 ml-3"
                            >
                            View details
                            </button>
                        </label>

                        <input
                        type="text"
                        value={agg_func_id}
                        readOnly           
                        onFocus={() => setActiveField("agg_func_id")}
                        placeholder="Aggregation Function"
                        disabled={fieldsLocked}
                        className={`w-full p-3 pl-4 pr-10 border rounded-lg text-sm transition-all ease-in-out duration-300 shadow-inner appearance-none
                        ${
                        !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : isEditMode && (activeField === "agg_func_id" || modifiedFields.agg_func_id)
                                ? "border-green-500 focus:ring-2 focus:ring-green-500 bg-white text-black"
                                : "border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white text-black"
                        }`}
                        />
                    </div>
                    {showAggDialog && (
                    <AggDetailsDialog
                        title="Aggregation Function Details"
                        onConfirm={closeAggDialog}
                        message={
                        selectedAggRows.length > 0 ? (
                            <div className="w-full overflow-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                <tr>
                                    <th className="border-b py-2 text-sm font-medium">to_granularity</th>
                                    <th className="border-b py-2 text-sm font-medium">description</th>
                                </tr>
                                </thead>
                        
                                <tbody>
                                {selectedAggRows.map((row, index) => (
                                    <tr key={index}>
                                    <td className="py-2 text-sm">{row.to_granularity}</td>
                                    <td className="py-2 text-sm">{row.description}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            </div>
                        ) : (
                            <div className="text-sm text-gray-600">No details available for this Agg Function.</div>
                        )
                        }
                          
                    />
                    )}

                </div>
            </div>

            </section>
        </div>
        </div>
        </form>
    )
};

export default MetricForm;